import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';
import { ComponentContext } from '../../ContextWrappers/NRComponentContextWrapper';
import { UserContext } from '../../App';
import { useParams } from 'react-router-dom';
import { getToken } from '../../services/auth';
import { useAuth0 } from '@auth0/auth0-react';
import { getDataFromTurbinePPComposedAssetId } from '../../utils/utils';
import { getChatEndpointUrl, submitFeedback, getSessionFeedback, deleteFeedback } from './utils/apiUtils';
import { useSessionId } from './hooks/useSessionId';
import { useChatStream } from './hooks/useChatStream';
import { buildChatPayload } from './utils/messageUtils';
import { processSSEStream } from './utils/sseStreamProcessor';
import WelcomeScreen from './components/WelcomeScreen';
import MessageItem from './components/MessageItem';
import ChatInput from './components/ChatInput';
import NRAIMorphSpinner from '../NRAIMorphSpinner/NRAIMorphSpinner';
import NRAIThinkingButton from '../NRAIThinkingButton/NRAIThinkingButton';
import { getAuthorText, COMPLETION_TEXT } from './authorMapping';
import NRResizableDrawer from '../NRResizableDrawer/NRResizableDrawer';
import NRRiseAiIcon from '../../NRIcons/NRRiseAiIcon';

const DrawerMainContainer = Box;
const DrawerContent = Box;

function NRRiseAiChatDrawer({ open, onClose }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { componentId: composedAssetId } = useParams();
  // Extract just the asset ID (without revision) from the composed asset ID
  const assetId = composedAssetId ? getDataFromTurbinePPComposedAssetId(composedAssetId).assetId : null;
  const { asset } = useContext(ComponentContext);
  const { currentOrgId } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const inputRef = useRef(null);
  const [spinners, setSpinners] = useState([]);
  const allEventsRef = useRef([]);
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const lastProcessedInvocationId = useRef(null);
  const processedChunkIds = useRef(new Set());
  const currentSpinnerIdRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const messagesLengthRef = useRef(0);
  const [feedbackMap, setFeedbackMap] = useState(new Map()); // Map<eventId, feedback>
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const drawerContentRef = useRef(null);

  // Use custom hook for session management
  const { sessionId, isLoadingSession, sessionError } = useSessionId(open, assetId);

  // Use custom hook for chat stream event handling
  const { processSSEEvent } = useChatStream({
    setMessages,
    allEventsRef,
    processedChunkIds,
    lastProcessedInvocationId,
    onAuthorChange: (author) => {
      // Update the current active spinner's author (find the most recent active spinner)
      setSpinners(prev => {
        // Find the last active spinner (most recent one that's still active)
        const activeSpinners = prev.filter(s => s.isActive);
        if (activeSpinners.length > 0) {
          const lastActiveSpinner = activeSpinners[activeSpinners.length - 1];
          return prev.map(spinner => 
            spinner.id === lastActiveSpinner.id
              ? { ...spinner, author }
              : spinner
          );
        }
        return prev;
      });
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    messagesLengthRef.current = messages.length;
  }, [messages]);

  // Check if content is scrollable and update gradient visibility
  const checkScrollable = useCallback(() => {
    if (drawerContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = drawerContentRef.current;
      // Show gradient if there's more content below (not scrolled to bottom)
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10; // 10px threshold
      setShowBottomGradient(isScrollable && !isAtBottom);
    }
  }, []);

  useEffect(() => {
    const contentElement = drawerContentRef.current;
    if (contentElement) {
      checkScrollable();
      contentElement.addEventListener('scroll', checkScrollable);
      // Also check on resize
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(contentElement);
      
      return () => {
        contentElement.removeEventListener('scroll', checkScrollable);
        resizeObserver.disconnect();
      };
    }
  }, [checkScrollable, messages]);

  // Load feedback when sessionId is available and drawer is open
  useEffect(() => {
    if (!open || !sessionId || isLoadingSession) return;

    const loadFeedback = async () => {
      try {
        let jwtToken = null;
        try {
          jwtToken = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE
          });
        } catch (authError) {
          enqueueSnackbar('An error occurred', { variant: 'error' }); 
          jwtToken = getToken();
        }

        if (!jwtToken) {
          return;
        }

        const feedbackRecords = await getSessionFeedback(sessionId, jwtToken, assetId || '');
        
        // Create a map of event_id -> feedback
        const newFeedbackMap = new Map();
        feedbackRecords.forEach(feedback => {
          newFeedbackMap.set(feedback.event_id, feedback);
        });
        setFeedbackMap(newFeedbackMap);
      } catch (error) {
        // Silently handle feedback loading errors
        enqueueSnackbar('An error occurred', { variant: 'error' });
      }
    };

    loadFeedback();
  }, [open, sessionId, isLoadingSession, getAccessTokenSilently, assetId, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const handleSendMessage = useCallback(async (skipUserMessage = false, messageTextOverride = null) => {
    const currentInputValue = inputRef.current?.value || '';
    const messageText = messageTextOverride !== null 
      ? messageTextOverride 
      : (skipUserMessage ? (lastUserMessageRef.current || currentInputValue.trim()) : currentInputValue.trim());
    
    if (!messageText.trim() || isStreaming) return;

    // Get JWT token early - prefer Auth0 token, fallback to localStorage
    let jwtToken = null;
    try {
      jwtToken = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE
      });
    } catch (authError) {
      enqueueSnackbar('An error occurred', { variant: 'error' });
      jwtToken = getToken();
    }

    if (!jwtToken) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date()
        }
      ]);
      return;
    }

    // Ensure we have a session ID before sending
    if (!sessionId) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date()
        }
      ]);
      return;
    }

    // Ensure we have a valid message text
    const trimmedMessageText = messageText.trim();
    if (!trimmedMessageText) {
      return;
    }

    const userMessage = {
      role: 'user',
      content: trimmedMessageText,
      timestamp: new Date()
    };
    
    // Store the user message for potential retry
    lastUserMessageRef.current = trimmedMessageText;
    
    let userMessageIndex;
    if (!skipUserMessage) {
      setMessages(prev => {
        userMessageIndex = prev.length;
        messagesLengthRef.current = prev.length + 1;
        return [...prev, userMessage];
      });
      if (inputRef.current) {
        const textareaElement = inputRef.current.tagName === 'TEXTAREA' 
          ? inputRef.current 
          : inputRef.current.querySelector('textarea') || inputRef.current;
        
        if (textareaElement) {
          textareaElement.value = '';
          const inputEvent = new Event('input', { bubbles: true });
          textareaElement.dispatchEvent(inputEvent);
        }
      }
    } else {
      // For retry, the user message is already in the array
      // Use the ref to get current length (updated by useEffect)
      userMessageIndex = messagesLengthRef.current - 1;
    }
    
    // Create a new spinner for this stream, associated with this user message
    const spinnerId = `spinner-${uuidv4()}`;
    currentSpinnerIdRef.current = spinnerId;
    setSpinners(prev => [
      ...prev,
      {
        id: spinnerId,
        author: null,
        isActive: true,
        completed: false,
        userMessageIndex
      }
    ]);
    
    setIsStreaming(true);
    // Reset tracking for new message
    lastProcessedInvocationId.current = null;
    processedChunkIds.current.clear();

    try {
      // Close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Build payload
      const payload = buildChatPayload(messageText, sessionId, jwtToken);

      // Create assistant message placeholder
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: '',
          timestamp: new Date()
        }
      ]);

      // Get chat endpoint URL
      const chatUrl = getChatEndpointUrl();

      const response = await fetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Authorization': `Bearer ${jwtToken}`,
          'X-Asset-Id': assetId || ''
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`HTTP error! status: ${response.status}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Store spinner ID in a local variable to ensure it persists through the stream
      const streamSpinnerId = currentSpinnerIdRef.current;
      
      // Process SSE stream
      await processSSEStream(response, {
        processSSEEvent,
        setIsStreaming,
        allEventsRef,
        onStreamStart: () => {
          setIsStreaming(true);
        },
        onStreamEnd: () => {
          setIsStreaming(false);
          // Mark the current spinner as completed using the stored ID
          if (streamSpinnerId) {
            setSpinners(prev => prev.map(spinner => 
              spinner.id === streamSpinnerId
                ? { ...spinner, isActive: false, completed: true, completionText: COMPLETION_TEXT }
                : spinner
            ));
          }
          currentSpinnerIdRef.current = null;
        },
        onStreamError: (errorEvent) => {
          if (errorEvent.isPartialResponse || 
              errorEvent.isConnectionLost || 
              errorEvent.isEmptyToolResponse ||
              errorEvent.isMalformedResponse ||
              errorEvent.isStreamError ||
              errorEvent.isStreamProcessingError) {
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                newMessages[newMessages.length - 1] = {
                  ...lastMessage,
                  content: 'Sorry, I encountered an error. Please try again.',
                  retryable: true
                };
              }
              return newMessages;
            });
          }
        }
      });
    } catch (error) {
      const errorMessage = 'Sorry, I encountered an error. Please try again.';

      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
          return [
            ...newMessages.slice(0, -1),
            {
              role: 'assistant',
              content: errorMessage,
              retryable: true,
              timestamp: new Date()
            }
          ];
        }
        return newMessages;
      });
      setIsStreaming(false);
      // Mark the current spinner as completed on error and show an error label
      // Find the most recent active spinner to mark as completed
      setSpinners(prev => {
        const activeSpinners = prev.filter(s => s.isActive);
        if (activeSpinners.length > 0) {
          const lastActiveSpinner = activeSpinners[activeSpinners.length - 1];
          return prev.map(spinner => 
            spinner.id === lastActiveSpinner.id
              ? { ...spinner, isActive: false, completed: true, completionText: 'Error' }
              : spinner
          );
        }
        return prev;
      });
      currentSpinnerIdRef.current = null;
    }
  }, [isStreaming, sessionId, getAccessTokenSilently, assetId, processSSEEvent]);

  // No need for handleInputChange - input is uncontrolled
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handlePromptClick = useCallback((promptText) => {
    if (inputRef.current) {
      inputRef.current.value = promptText;
      const inputEvent = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(inputEvent);
      inputRef.current.focus();
    }
  }, []);

  const handleRetry = async (messageIndex) => {
    if (isStreaming) return;
    
    let messageToRetry = null;
    
    if (messageIndex > 0) {
      const userMessage = messages[messageIndex - 1];
      if (userMessage && userMessage.role === 'user') {
        messageToRetry = userMessage.content;
      }
    }
    
    if (!messageToRetry && lastUserMessageRef.current) {
      messageToRetry = lastUserMessageRef.current;
    }
    
    if (!messageToRetry) {
      return;
    }
    
    lastUserMessageRef.current = messageToRetry;
    
    setMessages(prev => prev.filter((_, idx) => idx !== messageIndex));
    
    const userMessage = {
      role: 'user',
      content: messageToRetry,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // Use a small delay to ensure state updates, then send
    setTimeout(() => {
      handleSendMessage(true);
    }, 100);
  };

  const handleFeedbackSubmit = useCallback(async (sessionId, eventId, rating, reason) => {
    try {
      let jwtToken = null;
      try {
        jwtToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        });
      } catch (authError) {
        enqueueSnackbar('An error occurred', { variant: 'error' });
        jwtToken = getToken();
      }

      if (!jwtToken) {
        enqueueSnackbar('An error occurred', { variant: 'error' });
        return;
      }

      const feedbackRecord = await submitFeedback(sessionId, eventId, rating, reason, jwtToken, assetId || '');
      
      // Update feedback map
      setFeedbackMap(prev => {
        const newMap = new Map(prev);
        newMap.set(eventId, feedbackRecord);
        return newMap;
      });

      // Show success toast
      enqueueSnackbar('Thank you for your feedback!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to submit feedback', { variant: 'error' });
    }
  }, [getAccessTokenSilently, assetId, enqueueSnackbar]);

  const handleFeedbackDelete = useCallback(async (sessionId, eventId) => {
    try {
      let jwtToken = null;
      try {
        jwtToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        });
      } catch (authError) {
        enqueueSnackbar('An error occurred', { variant: 'error' });
        jwtToken = getToken();
      }

      if (!jwtToken) {
        enqueueSnackbar('An error occurred', { variant: 'error' });
        return;
      }

      await deleteFeedback(sessionId, eventId, jwtToken, assetId || '');
      
      // Update feedback map
      setFeedbackMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(eventId);
        return newMap;
      });
    } catch (error) {
      enqueueSnackbar('Failed to remove feedback', { variant: 'error' });
    }
  }, [getAccessTokenSilently, assetId, enqueueSnackbar]);

  return (
    <NRResizableDrawer 
      open={open} 
      onClose={onClose} 
      minWidth={700} 
      width={'66%'} 
      maxWidth={'95%'}
      title='RiseAI' 
      titleIcon={<NRRiseAiIcon color={theme.palette.riseAiPurple} />}
      contentPadding={0} 
      storageKey="RiseAIChatDrawer"
    >
      <DrawerMainContainer sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Scrollable messages area wrapper with gradient overlay */}
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Scrollable messages area */}
          <DrawerContent 
            ref={drawerContentRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: theme.spacing(6),
              backgroundColor: theme.palette.background.default,
              display: 'flex',
              flexDirection: 'column',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            {messages.length === 0 ? (
              <WelcomeScreen
                isLoadingSession={isLoadingSession}
                sessionError={sessionError}
                onPromptClick={handlePromptClick}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {messages.map((message, index) => {
                  const spinner =
                    message.role === 'user'
                      ? spinners.find(s => s.userMessageIndex === index)
                      : null;

                  // Get feedback for this message (use eventId from raw data)
                  const eventId = message.eventId;
                  const feedback = eventId ? feedbackMap.get(eventId) : null;

                  return (
                    <React.Fragment key={index}>
                      <MessageItem
                        message={message}
                        showHeader={
                          message.role === 'assistant' &&
                          (index === 0 || messages[index - 1].role !== 'assistant')
                        }
                        onRetry={message.retryable ? () => handleRetry(index) : undefined}
                        feedback={feedback}
                        onFeedbackSubmit={handleFeedbackSubmit}
                        onFeedbackDelete={handleFeedbackDelete}
                        sessionId={sessionId}
                        eventId={eventId}
                        onButtonClick={(value) => handleSendMessage(false, value)}
                      />
                      {spinner && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <NRAIMorphSpinner
                            key={spinner.id}
                            size={16}
                            text={
                              <NRAIThinkingButton
                                isThinking={!(spinner.completed && spinner.completionText)}
                                label={
                                  spinner.completed && spinner.completionText
                                    ? spinner.completionText
                                    : getAuthorText(spinner.author)
                                }
                                thinkingLabel={getAuthorText(spinner.author)}
                                color={theme.palette.text.secondary}
                                showChevron={false}
                                asText={true}
                              />
                            }
                            complete={spinner.completed}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              <div ref={messagesEndRef} />
            </Box>
          )}
          </DrawerContent>
          
          {/* Bottom gradient fade overlay - fixed at bottom of viewport */}
          {showBottomGradient && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: theme.spacing(8), // 64px fade height
                background: `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.default} 100%)`,
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          )}
        </Box>

        {/* Fixed ChatInput at bottom */}
        <ChatInput
          inputRef={inputRef}
          onSend={handleSendMessage}
          onKeyDown={handleKeyDown}
          isLoading={isStreaming}
          isLoadingSession={isLoadingSession}
          sessionId={sessionId}
        />
      </DrawerMainContainer>
    </NRResizableDrawer>
  );
}

export default NRRiseAiChatDrawer;
